/*
 * Generic settings form — single component used by the four spec'd
 * settings pages (pipeline / cors / environment / branding /
 * notifications / access-control). Renders /api/config + /api/settings
 * values relevant to each `view`. Mutations of platform settings go
 * through /api/settings PUT which already exists; this page reads the
 * current state and links to the writable surfaces where they exist.
 */

import { useEffect, useState } from "react";
import { SkeletonTable } from "../components/Skeleton.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { Link } from "react-router-dom";
import { api, ApiError, type AppSettings } from "../lib/api.js";

type SettingsView = "pipeline" | "cors" | "environment" | "branding" | "notifications" | "access-control";

const VIEW_META: Record<SettingsView, { title: string; blurb: string; configKeys: string[]; settingKeys: string[] }> = {
  pipeline: {
    title: "Pipeline settings",
    blurb: "Rollup strictness, ingestion strictness, dead-letter retention. Read from active runtime config.",
    configKeys: ["startupModule", "warehouseProvider", "warehouseQueryLimit"],
    settingKeys: ["startupModule", "warehouseProvider", "warehouseQueryLimit", "reportDefaults"]
  },
  cors: {
    title: "CORS settings",
    blurb: "Allowed origins for browser-side and embedded-app clients. Managed via env var TALLYSTREAM_CORS_ALLOWED_ORIGINS.",
    configKeys: [],
    settingKeys: []
  },
  environment: {
    title: "Environment settings",
    blurb: "Which deployment profile this instance runs as. Drives the EnvironmentBadge tone.",
    configKeys: ["environment"],
    settingKeys: []
  },
  branding: {
    title: "Branding settings",
    blurb: "Brand mark, accent colours, support contact. Currently read from the @tallystream/shared-brand package.",
    configKeys: [],
    settingKeys: []
  },
  notifications: {
    title: "Notification centre",
    blurb: "Outbound notification rules (email digests, webhook alerts on failure, paging escalation). Currently configured per workspace via the support secrets.",
    configKeys: [],
    settingKeys: []
  },
  "access-control": {
    title: "Access control settings",
    blurb: "MFA enforcement, session policy, IP allowlists. Mostly env-driven today.",
    configKeys: ["authType"],
    settingKeys: []
  }
};

export interface SettingsFormProps {
  view: SettingsView;
}

export default function SettingsForm({ view }: SettingsFormProps) {
  const meta = VIEW_META[view];
  const [config, setConfig] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (view === "notifications") return;  // notifications skips /api/config; uses its own editor
    api.config()
      .then((c) => setConfig(c as unknown as Record<string, unknown>))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [view]);

  if (view === "notifications") {
    return (
      <div className="page">
        <PageBreadcrumbs />
        <header className="page-head">
          <div>
            <h1>{meta.title}</h1>
            <p className="muted">{meta.blurb}</p>
          </div>
        </header>
        <NotificationsEditor />
      </div>
    );
  }

  if (view === "pipeline") {
    return (
      <div className="page">
        <PageBreadcrumbs />
        <header className="page-head">
          <div>
            <h1>{meta.title}</h1>
            <p className="muted">{meta.blurb}</p>
          </div>
        </header>
        <PipelineEditor />
      </div>
    );
  }

  if (view === "access-control") {
    return (
      <div className="page">
        <PageBreadcrumbs />
        <header className="page-head">
          <div>
            <h1>{meta.title}</h1>
            <p className="muted">{meta.blurb}</p>
          </div>
        </header>
        <AccessControlEditor />
      </div>
    );
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>{meta.title}</h1>
          <p className="muted">{meta.blurb}</p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {!config && <SkeletonTable rows={5} columns={4} />}

      {config && (
        <section className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>Active runtime values</h2>
          {meta.configKeys.length === 0 && meta.settingKeys.length === 0 ? (
            <p className="muted">
              {view === "cors" && "CORS origins are configured via the TALLYSTREAM_CORS_ALLOWED_ORIGINS environment variable on the API process. Update the env and restart to take effect."}
              {view === "branding" && "Brand assets live in packages/shared-brand. To customise, update that package and rebuild."}
              {/* notifications view exits early at the top of the component, so it never reaches this branch — kept here for completeness only. */}
            </p>
          ) : (
            <dl className="dl-grid">
              {meta.configKeys.map((k) => {
                const v = (config as Record<string, unknown>)[k];
                return (
                  <span key={k} style={{ display: "contents" }}>
                    <dt className="mono">{k}</dt>
                    <dd className="mono">{v == null ? <span className="muted">—</span> : typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                  </span>
                );
              })}
              {meta.settingKeys.map((k) => {
                const v = (config.settings as Record<string, unknown>)?.[k];
                return (
                  <span key={k} style={{ display: "contents" }}>
                    <dt className="mono">settings.{k}</dt>
                    <dd className="mono">{v == null ? <span className="muted">—</span> : typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                  </span>
                );
              })}
            </dl>
          )}
          <p className="muted" style={{ marginTop: 16, fontSize: 12 }}>
            To change runtime config, edit the platform's environment variables and restart, or use the
            existing {" "}
            <Link to="/admin/administration/settings/general">Platform settings</Link>{" "}
            page where the value is exposed for editing.
          </p>
        </section>
      )}
    </div>
  );
}

/*
 * Notifications editor — backed by /api/notifications/settings.
 * Workspace-scoped. Saves channels (email recipients, webhook URL) and
 * which event types route to each channel.
 */

const EVENT_TYPES: Array<{ key: string; label: string }> = [
  { key: "job.failed",              label: "A job failed" },
  { key: "dlq.added",               label: "An envelope was dead-lettered" },
  { key: "sync.completed",          label: "A sync completed (digest)" },
  { key: "reconciliation.variance", label: "Reconciliation found a variance" },
  { key: "plan.over-quota",         label: "Workspace went over a plan quota" }
];

function NotificationsEditor() {
  const [emailTo, setEmailTo] = useState<string>("");
  const [webhookUrl, setWebhookUrl] = useState<string>("");
  const [webhookSecret, setWebhookSecret] = useState<string>("");
  const [subs, setSubs] = useState<Record<string, { email: boolean; webhook: boolean }>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getNotificationSettings()
      .then((s) => {
        setEmailTo((s.channels.email?.to ?? []).join(", "));
        setWebhookUrl(s.channels.webhook?.url ?? "");
        setWebhookSecret(s.channels.webhook?.secret ?? "");
        const next: Record<string, { email: boolean; webhook: boolean }> = {};
        for (const ev of EVENT_TYPES) {
          const channels = (s.subscriptions[ev.key]?.channels ?? []) as string[];
          next[ev.key] = { email: channels.includes("email"), webhook: channels.includes("webhook") };
        }
        setSubs(next);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  async function save() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const channels: Record<string, unknown> = {};
      const emailRecipients = emailTo.split(",").map((s) => s.trim()).filter(Boolean);
      if (emailRecipients.length) channels.email = { to: emailRecipients };
      if (webhookUrl.trim()) channels.webhook = { url: webhookUrl.trim(), ...(webhookSecret ? { secret: webhookSecret } : {}) };
      const subscriptions: Record<string, { channels: string[] }> = {};
      for (const [evKey, sel] of Object.entries(subs)) {
        const ch: string[] = [];
        if (sel.email) ch.push("email");
        if (sel.webhook) ch.push("webhook");
        if (ch.length) subscriptions[evKey] = { channels: ch };
      }
      await api.setNotificationSettings({ channels, subscriptions });
      setNotice("Saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <>
      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Channels</h2>
        <div style={{ display: "grid", gap: 12 }}>
          <label className="transfer-filter">
            Email recipients (comma-separated)
            <input type="text" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="ops@example.com, oncall@example.com" />
          </label>
          <label className="transfer-filter">
            Webhook URL
            <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://hooks.example.com/tallystream" />
          </label>
          <label className="transfer-filter">
            Webhook signing secret (optional)
            <input type="text" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder="HMAC secret" />
          </label>
        </div>
      </section>

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Event subscriptions</h2>
        <table className="transfer-table">
          <thead><tr><th>Event</th><th style={{ textAlign: "center" }}>Email</th><th style={{ textAlign: "center" }}>Webhook</th></tr></thead>
          <tbody>
            {EVENT_TYPES.map((ev) => {
              const sel = subs[ev.key] ?? { email: false, webhook: false };
              return (
                <tr key={ev.key}>
                  <td>
                    <div>{ev.label}</div>
                    <code style={{ fontSize: 11, color: "var(--text-muted)" }}>{ev.key}</code>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" checked={sel.email} onChange={(e) => setSubs((s) => ({ ...s, [ev.key]: { ...sel, email: e.target.checked } }))} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" checked={sel.webhook} onChange={(e) => setSubs((s) => ({ ...s, [ev.key]: { ...sel, webhook: e.target.checked } }))} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
        {busy ? "Saving…" : "Save notification settings"}
      </button>
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        Settings persist immediately; the dispatcher worker (notifications-dispatcher)
        picks up changes within one tick (default 30s) and routes events to the
        configured channels. Test delivery from <Link to="/admin/operations/smtp">SMTP settings</Link>.
      </p>
    </>
  );
}

/*
 * Pipeline editor — editable form over the AppSettings fields that govern
 * rollup, ingestion, and dead-letter behaviour. Loads current settings,
 * merges user edits onto the existing object (PUT /api/settings expects a
 * full AppSettings), saves, and reloads.
 *
 * "Takes effect immediately" is honest for everything here: the API reads
 * settings live (no restart needed for warehouseQueryLimit, rollup modes,
 * reportDefaults). startupModule + warehouseProvider are exposed read-only
 * because they are deploy-shape concerns.
 */
function PipelineEditor() {
  const [original, setOriginal] = useState<AppSettings | null>(null);
  const [draft, setDraft] = useState<Partial<AppSettings>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.settings()
      .then((s) => { setOriginal(s); setDraft({}); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  if (error) return <div className="ts-alert ts-alert-error">{error}</div>;
  if (!original) return <SkeletonTable rows={6} columns={2} />;

  const merged: AppSettings = { ...original, ...draft };
  const reportDefaults = (draft.reportDefaults ?? original.reportDefaults) ?? { defaultProgramId: "", defaultIndicatorId: "", defaultPeriodId: "LAST_MONTH" };

  async function save() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const next = await api.updateSettings(merged);
      setOriginal(next);
      setDraft({});
      setNotice("Saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <>
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Editable</h2>
        <div style={{ display: "grid", gap: 12 }}>
          <label className="transfer-filter">
            Warehouse query limit
            <input
              type="number" min={1} max={10000}
              value={merged.warehouseQueryLimit}
              onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, warehouseQueryLimit: Number(e.target.value) || 0 }))}
            />
          </label>
          <label className="transfer-filter">
            Rollup refresh mode
            <select
              value={merged.rollupRefreshMode ?? "incremental"}
              onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, rollupRefreshMode: e.target.value as AppSettings["rollupRefreshMode"] }))}
            >
              <option value="incremental">incremental</option>
              <option value="scheduled">scheduled</option>
              <option value="manual">manual</option>
            </select>
          </label>
          <label className="transfer-filter">
            Rollup strictness
            <select
              value={merged.rollupStrictness ?? "strict"}
              onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, rollupStrictness: e.target.value as AppSettings["rollupStrictness"] }))}
            >
              <option value="strict">strict</option>
              <option value="permissive">permissive</option>
            </select>
          </label>
          <label className="transfer-filter">
            Aggregate validation strictness
            <select
              value={merged.aggregateValidationStrictness ?? "strict"}
              onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, aggregateValidationStrictness: e.target.value as AppSettings["aggregateValidationStrictness"] }))}
            >
              <option value="strict">strict</option>
              <option value="warning">warning</option>
            </select>
          </label>
          <label className="transfer-filter">
            Period rollup mode
            <select
              value={merged.periodRollupMode ?? "monthly-to-quarterly-yearly"}
              onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, periodRollupMode: e.target.value as AppSettings["periodRollupMode"] }))}
            >
              <option value="monthly-to-quarterly-yearly">monthly-to-quarterly-yearly</option>
              <option value="flat">flat</option>
            </select>
          </label>
          <label className="transfer-filter">
            Org-unit rollup mode
            <select
              value={merged.orgUnitRollupMode ?? "hierarchy"}
              onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, orgUnitRollupMode: e.target.value as AppSettings["orgUnitRollupMode"] }))}
            >
              <option value="hierarchy">hierarchy</option>
              <option value="flat">flat</option>
            </select>
          </label>

          <fieldset style={{ border: "1px solid var(--border)", padding: 12, borderRadius: 6 }}>
            <legend style={{ padding: "0 6px" }}>Report defaults</legend>
            <label className="transfer-filter">
              Default program ID
              <input
                type="text"
                value={reportDefaults.defaultProgramId}
                onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, reportDefaults: { ...reportDefaults, defaultProgramId: e.target.value } }))}
              />
            </label>
            <label className="transfer-filter">
              Default indicator ID
              <input
                type="text"
                value={reportDefaults.defaultIndicatorId}
                onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, reportDefaults: { ...reportDefaults, defaultIndicatorId: e.target.value } }))}
              />
            </label>
            <label className="transfer-filter">
              Default period ID
              <input
                type="text"
                value={reportDefaults.defaultPeriodId}
                onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, reportDefaults: { ...reportDefaults, defaultPeriodId: e.target.value } }))}
              />
            </label>
          </fieldset>

          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save pipeline settings"}
          </button>
          <p className="muted" style={{ fontSize: 12 }}>
            Changes take effect immediately — the API reads these values live, no restart required.
          </p>
        </div>
      </section>

      <section className="empty-state-card">
        <h2 style={{ marginTop: 0 }}>Read-only (deploy shape)</h2>
        <dl className="dl-grid">
          <span style={{ display: "contents" }}><dt className="mono">startupModule</dt><dd className="mono">{merged.startupModule}</dd></span>
          <span style={{ display: "contents" }}><dt className="mono">warehouseProvider</dt><dd className="mono">{merged.warehouseProvider}</dd></span>
        </dl>
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          These are deploy artefacts — change via env-var + restart. Edit on the
          full <Link to="/admin/administration/settings/general">Platform settings</Link> page if
          available.
        </p>
      </section>
    </>
  );
}

/*
 * Access-control editor — RBAC mode is the one runtime-tunable here. MFA,
 * session policy, and IP allowlists are env-driven (and noted as such on
 * the page) until a future release wires them to platform_settings.
 */
function AccessControlEditor() {
  const [original, setOriginal] = useState<AppSettings | null>(null);
  const [draft, setDraft] = useState<Partial<AppSettings>>({});
  const [config, setConfig] = useState<{ config: Record<string, unknown> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.settings(), api.config()])
      .then(([s, c]) => { setOriginal(s); setDraft({}); setConfig(c as unknown as { config: Record<string, unknown> }); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  if (error) return <div className="ts-alert ts-alert-error">{error}</div>;
  if (!original) return <SkeletonTable rows={4} columns={2} />;

  const merged: AppSettings = { ...original, ...draft };
  const authType = (config?.config?.authType ?? "—") as string;

  async function save() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const next = await api.updateSettings(merged);
      setOriginal(next);
      setDraft({});
      setNotice("Saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <>
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Editable</h2>
        <label className="transfer-filter">
          Role mode
          <select
            value={merged.roleMode}
            onChange={(e) => setDraft((d: Partial<AppSettings>) => ({ ...d, roleMode: e.target.value as AppSettings["roleMode"] }))}
          >
            <option value="strict">strict — every action requires an explicit permission</option>
            <option value="permissive">permissive — workspace owners shortcut to any workspace permission</option>
          </select>
        </label>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()} style={{ marginTop: 8 }}>
          {busy ? "Saving…" : "Save access-control settings"}
        </button>
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Takes effect on the next request — no restart required.
        </p>
      </section>

      <section className="empty-state-card">
        <h2 style={{ marginTop: 0 }}>Env-driven (read-only)</h2>
        <dl className="dl-grid">
          <span style={{ display: "contents" }}><dt className="mono">authType</dt><dd className="mono">{authType}</dd></span>
        </dl>
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          MFA enforcement, session policy and IP allowlists are configured via
          API env-vars on the operator host. A future release will move these
          into <code>platform_settings</code> so they can be edited here without
          a restart.
        </p>
      </section>
    </>
  );
}
